import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    urgency: 'normal'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const submissionData = new FormData(e.target);
      submissionData.append("access_key", "d2ef38f9-522d-48c8-9237-10448a0aceb9");
      submissionData.append("subject", `New Consultation Request from ${formData.name}`);
      submissionData.append("from_name", "VASTECH Website");

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: submissionData
      });

      const data = await response.json();
      if (data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '', urgency: 'normal' });
        e.target.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="bg-white p-8 md:p-12 border-2 border-slate-100 shadow-2xl relative"
    >
      <div className="absolute -top-1 -right-1 w-12 h-12 border-t-4 border-r-4 border-[#91756A]"></div>
      
      <h2 className="text-3xl font-bold text-[#4D4C4A] italic tracking-tighter uppercase mb-10">Request Consultation</h2>

      {submitStatus === 'success' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8 p-6 bg-green-50 border-l-4 border-green-500 text-green-800 italic">
          Thank you! Your request has been submitted successfully. We'll contact you within 24 hours.
        </motion.div>
      )}

      {submitStatus === 'error' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8 p-6 bg-red-50 border-l-4 border-red-500 text-red-800 italic">
          There was an error submitting your request. Please try again or call us directly.
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Full Name *</label>
            <input
              type="text" name="name" value={formData.name} onChange={handleInputChange} required
              className="w-full bg-slate-50 border-b-2 border-slate-200 px-0 py-3 focus:border-[#91756A] outline-none transition-colors italic font-medium text-[#4D4C4A]"
              placeholder="John Doe"
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Company</label>
            <input
              type="text" name="company" value={formData.company} onChange={handleInputChange}
              className="w-full bg-slate-50 border-b-2 border-slate-200 px-0 py-3 focus:border-[#91756A] outline-none transition-colors italic font-medium text-[#4D4C4A]"
              placeholder="Organization Name"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Email Address *</label>
            <input
              type="email" name="email" value={formData.email} onChange={handleInputChange} required
              className="w-full bg-slate-50 border-b-2 border-slate-200 px-0 py-3 focus:border-[#91756A] outline-none transition-colors italic font-medium text-[#4D4C4A]"
              placeholder="john@example.com"
            />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Phone Number</label>
            <input
              type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
              className="w-full bg-slate-50 border-b-2 border-slate-200 px-0 py-3 focus:border-[#91756A] outline-none transition-colors italic font-medium text-[#4D4C4A]"
              placeholder="+971 -- --- -- --"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Service Type *</label>
            <select
              name="service" value={formData.service} onChange={handleInputChange} required
              className="w-full bg-slate-50 border-b-2 border-slate-200 px-0 py-3 focus:border-[#91756A] outline-none transition-colors italic font-bold text-[#4D4C4A] appearance-none cursor-pointer"
            >
              <option value="">Select a service</option>
              <option value="elevator-new">New Elevator Installation</option>
              <option value="elevator-modernization">Elevator Modernization</option>
              <option value="ac-maintenance">AC Maintenance Services</option>
              <option value="smart-home">Smart Home Solutions</option>
              <option value="emergency">Emergency Service</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Urgency Level</label>
            <select
              name="urgency" value={formData.urgency} onChange={handleInputChange}
              className="w-full bg-slate-50 border-b-2 border-slate-200 px-0 py-3 focus:border-[#91756A] outline-none transition-colors italic font-bold text-[#4D4C4A] appearance-none cursor-pointer"
            >
              <option value="normal">Normal (48h)</option>
              <option value="urgent">Urgent (24h)</option>
              <option value="emergency">Emergency (Immediate)</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Project Details *</label>
          <textarea
            name="message" value={formData.message} onChange={handleInputChange} required rows={4}
            className="w-full bg-slate-50 border-b-2 border-slate-200 px-0 py-3 focus:border-[#91756A] outline-none transition-colors italic font-medium text-[#4D4C4A] resize-none"
            placeholder="Describe your requirements..."
          />
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#4D4C4A] hover:bg-[#3d3c3a] text-white py-8 rounded-none text-xs font-bold uppercase tracking-[0.4em] h-auto group transition-all duration-500"
        >
          {isSubmitting ? "Processing Request..." : (
            <span className="flex items-center justify-center">
              Submit Consultation Request
              <Icon name="ArrowRight" size={16} className="ml-4 group-hover:translate-x-2 transition-transform duration-300" />
            </span>
          )}
        </Button>
      </form>
    </motion.div>
  );
};

export default ContactForm;